// Calendarul elevului (/student/calendar), ca aplicația Calendar de pe iPhone: sus rândul comun
// (.pageToolbar) cu luna, Săpt. / Lună, ‹ Azi ›; dedesubt, direct pe pagină (fără panou):
//   Lună — grila Lu–Du cu punctele zilelor cu lecții și lista zilei alese (ReservationCardUI);
//   Săptămână — Lu–Du, orele în stânga, lecțiile ca blocuri colorate la ora lor.
// Glisarea stânga / dreapta și săgețile de pe tastatură trec la luna / săptămâna vecină.
// La apăsarea unei lecții se deschide aceeași fereastră ca până acum (onSelectEvent).
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
   addDays,
   addMonths,
   addWeeks,
   differenceInCalendarDays,
   format,
   isSameDay,
   isSameMonth,
   startOfDay,
   startOfMonth,
   startOfWeek,
} from "date-fns";
import { ro } from "date-fns/locale";

import { ButtonUI, ChoiceUI, ReservationCardUI } from "../UI";

const WEEK = { weekStartsOn: 1, locale: ro };
const WEEKDAYS = ["Lu", "Ma", "Mi", "Jo", "Vi", "Sâ", "Du"];
const VIEWS = [
   { value: "week", label: "Săpt." },
   { value: "month", label: "Lună" },
];
const VIEW_KEY = "studentCalendarView";
const PHONE = "(max-width: 767.98px)";
const SWIPE_PX = 48;
const DAY_START_HOUR = 7;
const DAY_END_HOUR = 21;

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const dayKey = (d) => d.toDateString();
const minutesOf = (d) => d.getHours() * 60 + d.getMinutes();
const isPhone = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(PHONE).matches;

function readView() {
   try {
      const v = window.localStorage.getItem(VIEW_KEY);
      if (VIEWS.some((o) => o.value === v)) return v;
   } catch (_) {}
   return isPhone() ? "month" : "week";
}

function saveView(v) {
   try {
      window.localStorage.setItem(VIEW_KEY, v);
   } catch (_) {}
}

// culoarea și numele stării: lecția galbenă, examenul albastru (verde admis, roșu respins…), anulata stinsă
function eventLook(event) {
   const item = event.cardItem || {};
   if (item.inactive) return { tone: "cancelled", label: "Anulată" };
   if (event.kind === "practicalExam") {
      const s = String(item.status || "SCHEDULED").toUpperCase();
      if (["PASSED", "PROMOTED", "PROMOVAT", "ADMIS"].includes(s)) return { tone: "passed", label: item.statusLabel };
      if (["FAILED", "RESPINS"].includes(s)) return { tone: "failed", label: item.statusLabel };
      if (s === "ABSENT") return { tone: "absent", label: item.statusLabel };
      if (["CANCELLED", "CANCELED", "ANULAT"].includes(s)) return { tone: "cancelled", label: item.statusLabel };
      return { tone: "exam", label: item.statusLabel };
   }
   return { tone: "lesson", label: item.statusLabel };
}

const RESULT_TONE = { exam: "info", passed: "success", failed: "danger" };

const timeRange = (event) => `${format(event.start, "HH:mm")} – ${format(event.end || event.start, "HH:mm")}`;

/** Evenimentele pe zile, în ordinea orei. */
function indexByDay(events) {
   const map = new Map();
   for (const event of events) {
      if (!event?.start) continue;
      const key = dayKey(event.start);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(event);
   }
   for (const list of map.values()) list.sort((a, b) => a.start - b.start);
   return map;
}

/**
 * Blocurile unei zile pe axa orelor: anulata care se suprapune cu una vie nu se mai vede
 * (ca până acum); cele care se suprapun stau una lângă alta (lane / lanes).
 */
function layoutDay(list) {
   const live = list.filter((e) => !e.cardItem?.inactive);
   const shown = list.filter((e) => !e.cardItem?.inactive || !live.some((l) => l.start < (e.end || e.start) && e.start < (l.end || l.start)));
   const out = [];
   let group = [];
   let groupEnd = 0;
   const flush = () => {
      const lanesEnd = [];
      const placed = group.map((e) => {
         let lane = lanesEnd.findIndex((end) => end <= e.start.getTime());
         if (lane < 0) lane = lanesEnd.length;
         lanesEnd[lane] = (e.end || e.start).getTime();
         return { event: e, lane };
      });
      placed.forEach((p) => out.push({ ...p, lanes: lanesEnd.length }));
      group = [];
   };
   for (const e of shown) {
      if (group.length && e.start.getTime() >= groupEnd) flush();
      group.push(e);
      groupEnd = Math.max(groupEnd, (e.end || e.start).getTime());
   }
   if (group.length) flush();
   return out;
}

function useNow() {
   const [now, setNow] = useState(() => new Date());
   useEffect(() => {
      const t = window.setInterval(() => setNow(new Date()), 60_000);
      return () => window.clearInterval(t);
   }, []);
   return now;
}

// ── blocul unei lecții pe axa orelor ──
const EventBlock = memo(function EventBlock({ event, lane, lanes, fromMin, onSelectEvent }) {
   const { tone } = eventLook(event);
   const top = minutesOf(event.start) - fromMin;
   const height = Math.max(24, ((event.end || event.start) - event.start) / 60000);
   const where = event.cardItem?.extra;
   return (
      <button
         type="button"
         className={`sCal__block is-${tone}`}
         style={{ "--top": top, "--len": height, "--lane": lane, "--lanes": lanes }}
         onClick={() => onSelectEvent?.(event)}
         aria-label={`${event.typeLabel}, ${timeRange(event)}${where ? `, ${where}` : ""}`}
      >
         <span className="sCal__blockTitle">{event.typeLabel}</span>
         <span className="sCal__blockTime">{format(event.start, "HH:mm")}</span>
         {where ? <span className="sCal__blockWhere">{where}</span> : null}
      </button>
   );
});

// ── orele: axa din stânga și câte o coloană pe zi ──
function Timeline({ days, eventsByDay, hours, now, onSelectEvent }) {
   const fromMin = hours[0] * 60;
   const toMin = (hours[hours.length - 1] + 1) * 60;
   const nowMin = minutesOf(now);
   const showNow = nowMin >= fromMin && nowMin <= toMin;
   return (
      <div className="sCal__timeline" style={{ "--hours": hours.length, "--cols": days.length }}>
         <div className="sCal__axis" aria-hidden="true">
            {hours.map((h) => (
               <span key={h} className="sCal__hour" style={{ "--top": (h - hours[0]) * 60 }}>
                  {String(h).padStart(2, "0")}:00
               </span>
            ))}
         </div>
         {days.map((day) => {
            const today = isSameDay(day, now);
            return (
               <div key={dayKey(day)} className={`sCal__col${today ? " is-today" : ""}`}>
                  {layoutDay(eventsByDay.get(dayKey(day)) || []).map(({ event, lane, lanes }) => (
                     <EventBlock
                        key={event.cardItem?.key || event.id}
                        event={event}
                        lane={lane}
                        lanes={lanes}
                        fromMin={fromMin}
                        onSelectEvent={onSelectEvent}
                     />
                  ))}
                  {today && showNow ? <span className="sCal__now" style={{ "--top": nowMin - fromMin }} aria-hidden="true" /> : null}
               </div>
            );
         })}
      </div>
   );
}

// ── un rând de zile (capul săptămânii, banda din vederea Zi) ──
function DayStrip({ days, now, onPick }) {
   return (
      <div className="sCal__strip" style={{ "--cols": days.length }}>
         {days.map((day, i) => {
            return (
               <button
                  key={dayKey(day)}
                  type="button"
                  className={[
                     "sCal__stripDay",
                     isSameDay(day, now) && "is-today",
                  ]
                     .filter(Boolean)
                     .join(" ")}
                  onClick={() => onPick(day)}
                  aria-label={cap(format(day, "EEEE, d MMMM", { locale: ro }))}
               >
                  <span className="sCal__stripWeekday">{WEEKDAYS[i % 7]}</span>
                  <span className="sCal__num">{format(day, "d")}</span>
               </button>
            );
         })}
      </div>
   );
}

// ── lista zilei alese (sub lună) ──
function DayAgenda({ day, list, onSelectEvent }) {
   return (
      <section className="sCal__agenda" aria-label="Lecțiile zilei">
         <h2 className="sCal__agendaTitle">{cap(format(day, "EEEE, d MMMM", { locale: ro }))}</h2>
         {list.length ? (
            <ReservationCardUI.List columns={1}>
               {list.map((event) => {
                  const { tone, label } = eventLook(event);
                  return (
                     <ReservationCardUI
                        key={event.cardItem?.key || event.id}
                        className={`sCal__card is-${tone}`}
                        tone={RESULT_TONE[tone]}
                        cancelled={tone === "cancelled"}
                        number={event.raw?.lessonNumber ?? null}
                        date={event.typeLabel}
                        time={timeRange(event)}
                        // fără numele instructorului (22.10.2026: nici serverul nu-l mai trimite elevului)
                        lines={[label, event.cardItem?.extra]}
                        onOpen={() => onSelectEvent?.(event)}
                     />
                  );
               })}
            </ReservationCardUI.List>
         ) : (
            <p className="sCal__empty">Nicio lecție în ziua aceasta.</p>
         )}
      </section>
   );
}

// ── luna: grila cu punctele zilelor ──
function MonthGrid({ month, selected, now, eventsByDay, onPick }) {
   const first = startOfWeek(startOfMonth(month), WEEK);
   const weeks = Math.ceil((differenceInCalendarDays(startOfMonth(addMonths(month, 1)), first)) / 7);
   const days = Array.from({ length: weeks * 7 }, (_, i) => addDays(first, i));
   return (
      <div className="sCal__month" style={{ "--weeks": weeks }}>
         {WEEKDAYS.map((w) => (
            <span key={w} className="sCal__monthWeekday" aria-hidden="true">
               {w}
            </span>
         ))}
         {days.map((day) => {
            const list = eventsByDay.get(dayKey(day)) || [];
            const inMonth = isSameMonth(day, month);
            return (
               <button
                  key={dayKey(day)}
                  type="button"
                  className={[
                     "sCal__cell",
                     !inMonth && "is-outside",
                     isSameDay(day, now) && "is-today",
                     isSameDay(day, selected) && "is-selected",
                  ]
                     .filter(Boolean)
                     .join(" ")}
                  onClick={() => onPick(day)}
                  aria-label={`${cap(format(day, "EEEE, d MMMM", { locale: ro }))}${list.length ? `, ${list.length} în calendar` : ""}`}
               >
                  <span className="sCal__num">{format(day, "d")}</span>
                  {list.length ? (
                     <>
                        <span className="sCal__dots" aria-hidden="true">
                           {list.slice(0, 3).map((e) => (
                              <span key={e.cardItem?.key || e.id} className={`sCal__dot is-${eventLook(e).tone}`} />
                           ))}
                        </span>
                        <span className="sCal__chips" aria-hidden="true">
                           {list.slice(0, 3).map((e) => (
                              <span key={e.cardItem?.key || e.id} className={`sCal__chip is-${eventLook(e).tone}`}>
                                 {format(e.start, "HH:mm")} {e.typeLabel}
                              </span>
                           ))}
                           {list.length > 3 ? <span className="sCal__more">+{list.length - 3}</span> : null}
                        </span>
                     </>
                  ) : null}
               </button>
            );
         })}
      </div>
   );
}

export default function StudentCalendar({ events = [], onSelectEvent }) {
   const now = useNow();
   const [view, setViewState] = useState(readView);
   const [selected, setSelected] = useState(() => startOfDay(new Date()));
   const [dir, setDir] = useState(0);
   const touch = useRef(null);

   const eventsByDay = useMemo(() => indexByDay(events), [events]);

   // orele de pe axă: 07–21, mai multe dacă o lecție iese din ele
   const hours = useMemo(() => {
      let from = DAY_START_HOUR;
      let to = DAY_END_HOUR;
      for (const e of events) {
         if (!e?.start) continue;
         from = Math.min(from, e.start.getHours());
         const end = e.end || e.start;
         to = Math.max(to, end.getHours() + (end.getMinutes() ? 1 : 0));
      }
      return Array.from({ length: to - from }, (_, i) => from + i);
   }, [events]);

   const setView = useCallback((v) => {
      setViewState(v);
      saveView(v);
   }, []);

   const step = useCallback(
      (delta) => {
         setDir(delta);
         setSelected((d) => (view === "month" ? addMonths(d, delta) : addWeeks(d, delta)));
      },
      [view],
   );

   const goToday = useCallback(() => {
      const today = startOfDay(new Date());
      setDir(today < selected ? -1 : today > selected ? 1 : 0);
      setSelected(today);
   }, [selected]);

   // săgețile de pe tastatură (nu când scrii sau alegi în ChoiceUI)
   useEffect(() => {
      const onKey = (e) => {
         if (e.altKey || e.ctrlKey || e.metaKey) return;
         if (e.target.closest?.("input, textarea, select, [contenteditable='true'], .popupUI, [role='dialog']")) return;
         if (e.key === "ArrowLeft") step(-1);
         else if (e.key === "ArrowRight") step(1);
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
   }, [step]);

   const onTouchStart = (e) => {
      const t = e.touches[0];
      touch.current = t ? { x: t.clientX, y: t.clientY } : null;
   };
   const onTouchEnd = (e) => {
      const start = touch.current;
      touch.current = null;
      const t = e.changedTouches[0];
      if (!start || !t) return;
      const dx = t.clientX - start.x;
      if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(t.clientY - start.y) * 1.5) return;
      step(dx < 0 ? 1 : -1);
   };

   const weekDays = useMemo(() => {
      const first = startOfWeek(selected, WEEK);
      return Array.from({ length: 7 }, (_, i) => addDays(first, i));
   }, [selected]);

   const title = cap(format(selected, "LLLL yyyy", { locale: ro }));
   const unit = view === "month" ? "Luna" : "Săptămâna";
   const pageKey = `${view}-${view === "month" ? format(selected, "yyyy-MM") : dayKey(weekDays[0])}`;

   const pageClass = `sCal__page${dir ? ` sCal__page--${dir > 0 ? "next" : "prev"}` : ""}`;

   const openDay = (day) => {
      setSelected(startOfDay(day));
      setView("month");
   };

   return (
      <div className={`sCal sCal--${view}`}>
         <div className="pageToolbar sCal__toolbar">
            <h1 className="sCal__title" aria-live="polite">
               {title}
            </h1>
            <div className="pageToolbar__controls sCal__controls">
               <ChoiceUI look="segmented" options={VIEWS} value={view} onChange={setView} aria-label="Vederea" className="sCal__views" />
               <ButtonUI size="lg" variant="subtle" icon="arrow" aria-label={`${unit} anterioară`} title={`${unit} anterioară`} onClick={() => step(-1)} />
               <ButtonUI size="lg" variant="subtle" onClick={goToday}>
                  Azi
               </ButtonUI>
               <ButtonUI size="lg" variant="subtle" icon="arrow" iconFlip aria-label={`${unit} următoare`} title={`${unit} următoare`} onClick={() => step(1)} />
            </div>
         </div>

         <div className="sCal__body" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            {view === "month" ? (
               <div key={pageKey} className={pageClass}>
                  <div className="sCal__monthWrap">
                     <MonthGrid month={selected} selected={selected} now={now} eventsByDay={eventsByDay} onPick={(d) => setSelected(startOfDay(d))} />
                     <DayAgenda day={selected} list={eventsByDay.get(dayKey(selected)) || []} onSelectEvent={onSelectEvent} />
                  </div>
               </div>
            ) : (
               <div className="sCal__hoursView">
                  <div key={dayKey(weekDays[0])} className={`sCal__head ${pageClass}`}>
                     <DayStrip days={weekDays} now={now} onPick={openDay} />
                  </div>
                  <div className="sCal__scroll">
                     <div key={pageKey} className={pageClass}>
                        <Timeline
                           days={weekDays}
                           eventsByDay={eventsByDay}
                           hours={hours}
                           now={now}
                           onSelectEvent={onSelectEvent}
                        />
                     </div>
                  </div>
               </div>
            )}
         </div>
      </div>
   );
}
